/**
 * Deferred fox-mask: vibe FoxChat saves the unmasked photo first, then this helper
 * generates the mask and patches `savedWantedPosters` + the public share mirror.
 *
 * Also used as a login-time backfill when the in-chat job never ran or failed
 * (common on new-user OAuth: user-doc provision races the save, or the edit API
 * errors and was previously swallowed with no retry).
 */

import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { applyUserPhotoFoxMask } from '../services/firebaseApiService';
import { persistImageUrlToStorage, writeSharedPosterMirror } from './collagePersistence';
import { normalizeSavedPostersList } from './savedPosterNormalize';
import { syncUserPublicPrimaryFromUserDocument } from './userPublicPrimarySync';

const inFlightKeys = new Set();

const clipUrl = (u) => (u && typeof u === 'string' && u.trim() ? u.trim() : null);

const sleep = (ms) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

/** True when the displayed cutout is still the stored unmasked original. */
export function posterNeedsFoxMask(poster) {
  if (!poster || typeof poster !== 'object') return false;
  const original = clipUrl(poster.userPhotoOriginalUrl)
    || clipUrl(poster.surveyAnswers?.user_photo_original);
  if (!original) return false;
  const displayed = clipUrl(poster.partnerCutout) || clipUrl(poster.userPhotoUrl);
  if (!displayed) return false;
  return displayed === original;
}

export function unmaskedUrlFromPoster(poster) {
  if (!poster || typeof poster !== 'object') return null;
  return clipUrl(poster.userPhotoOriginalUrl)
    || clipUrl(poster.surveyAnswers?.user_photo_original)
    || clipUrl(poster.userPhotoUrl)
    || clipUrl(poster.partnerCutout);
}

function patchPosterWithMaskedUrl(poster, maskedUrl) {
  const surveyAnswers = poster.surveyAnswers && typeof poster.surveyAnswers === 'object'
    ? { ...poster.surveyAnswers, user_photo: maskedUrl }
    : poster.surveyAnswers;
  return {
    ...poster,
    partnerCutout: maskedUrl,
    userPhotoUrl: maskedUrl,
    surveyAnswers,
  };
}

async function readPosterFromUserDoc(db, uid, posterId) {
  const userDocRef = doc(db, 'users', uid);
  const snap = await getDoc(userDocRef);
  const list = normalizeSavedPostersList(
    Array.isArray(snap.data()?.savedWantedPosters) ? snap.data().savedWantedPosters : [],
  );
  return { userDocRef, list, poster: list.find((p) => p.id === posterId) || null };
}

/**
 * @returns {Promise<{ poster?: object, skipped?: boolean, reason?: string }>}
 */
export async function applyFoxMaskToSavedPoster({ db, posterId, unmaskedUrl, uid, displayName = null }) {
  const key = `${uid || ''}:${posterId || ''}`;
  if (!db || !posterId || !unmaskedUrl || !uid) {
    return { skipped: true, reason: 'missing_args' };
  }
  if (inFlightKeys.has(key)) {
    return { skipped: true, reason: 'in_flight' };
  }
  inFlightKeys.add(key);

  try {
    let found = await readPosterFromUserDoc(db, uid, posterId);
    /* New-user OAuth: provision's user-doc write can land just after SAVE. Wait and re-read
       instead of dropping the mask on a missing row. */
    for (let attempt = 0; attempt < 4 && !found.poster; attempt += 1) {
      await sleep(700);
      found = await readPosterFromUserDoc(db, uid, posterId);
    }
    if (!found.poster) {
      console.warn('applyFoxMaskToSavedPoster: poster not on user doc yet', posterId);
      return { reason: 'poster_missing' };
    }
    if (!posterNeedsFoxMask(found.poster) && clipUrl(found.poster.partnerCutout) !== clipUrl(unmaskedUrl)) {
      return { skipped: true, reason: 'already_masked', poster: found.poster };
    }

    const maskedRaw = await applyUserPhotoFoxMask(unmaskedUrl);
    const maskedUrl = (await persistImageUrlToStorage(maskedRaw, 'user_photos', 'jpg')) || maskedRaw;
    if (!maskedUrl) return { reason: 'no_masked_url' };

    /* Re-read so we keep flags written while the edit API ran (primary, prize opt-in, …). */
    const latest = await readPosterFromUserDoc(db, uid, posterId);
    if (!latest.poster) return { reason: 'poster_missing_after_edit' };

    let patchedPoster = null;
    const updated = latest.list.map((p) => {
      if (p.id !== posterId) return p;
      patchedPoster = patchPosterWithMaskedUrl(p, maskedUrl);
      return patchedPoster;
    });
    if (!patchedPoster) return { reason: 'poster_missing_after_edit' };

    await updateDoc(latest.userDocRef, { savedWantedPosters: updated, updatedAt: new Date() });

    try {
      await writeSharedPosterMirror(db, patchedPoster);
    } catch (mirrorErr) {
      console.warn('applyFoxMaskToSavedPoster: sharedPosters update failed:', mirrorErr);
    }

    try {
      await syncUserPublicPrimaryFromUserDocument(db, uid, displayName);
    } catch (syncErr) {
      console.warn('applyFoxMaskToSavedPoster: userPublicPrimary sync failed:', syncErr);
    }

    return { poster: patchedPoster };
  } catch (err) {
    console.error('applyFoxMaskToSavedPoster failed:', err);
    return { reason: 'error' };
  } finally {
    inFlightKeys.delete(key);
  }
}
